import type { DocNetAgentProfile, DocNetEmailEvent, DocNetEmailStats } from 'src/@types/docnet';
import type { Agent } from 'src/@types/agents';

const ORG_ID = 'org-mock-001';

function makeAgent(overrides: Partial<Agent> & { id: string; name: string }): Agent {
  return {
    org_id: ORG_ID,
    description: '',
    system_prompt: '',
    document_types: [],
    skills: [],
    triggers: [],
    mcp_tools: [],
    config: {
      max_retries: 3,
      timeout_seconds: 120,
      log_interactions: true,
      require_approval: false,
      model: 'gpt-4o',
      temperature: 0.2,
      max_tokens: 4096,
    },
    priority: 5,
    is_enabled: true,
    is_system: false,
    is_default: false,
    memory_enabled: true,
    is_wizard: false,
    wizard_name: null,
    created_at: '2026-01-15T10:00:00Z',
    updated_at: '2026-03-05T14:30:00Z',
    ...overrides,
  } as Agent;
}

export const mockAgents: DocNetAgentProfile[] = [
  {
    agent: makeAgent({
      id: 'agent-001',
      name: 'InvoiceExtractor',
      description: 'Extracts header and line-item data from ingredient & packaging invoices, handles lot numbers, best-before dates, and allergen declaration fields.',
      document_types: ['INVOICE', 'CREDIT_NOTE'],
      skills: [
        { skill_type: 'document_extraction', is_enabled: true, config: { fields: ['invoice_number', 'date', 'total', 'line_items', 'lot_number', 'best_before', 'allergens'] }, description: 'Invoice field extraction with F&B-specific fields' },
      ],
      triggers: [
        { trigger_type: 'document_upload', conditions: [{ field: 'document_type', operator: 'eq', value: 'INVOICE' }], is_enabled: true, description: 'Trigger on invoice upload' },
      ],
      priority: 10,
    }),
    role: 'extractor',
    skills_summary: ['Invoice header extraction', 'Line-item parsing with lot/batch numbers', 'Allergen field detection', 'Best-before date extraction'],
    current_tasks: ['task-001', 'task-005'],
    completed_task_count: 247,
    approval_mode: 'auto',
    reports_to: 'agent-007',
    direct_reports: [],
    workload_percent: 72,
    last_active_at: '2026-03-09T08:45:00Z',
    performance: { success_rate: 97.2, avg_completion_hours: 0.8, tasks_per_week: 41, error_count: 7, streak_days: 14 },
  },
  {
    agent: makeAgent({
      id: 'agent-002',
      name: 'DocClassifier',
      description: 'Classifies supplier documents into categories: invoices, CoAs (Certificate of Analysis), spec sheets, organic certifications, delivery notes, and recall notices.',
      document_types: ['INVOICE', 'PURCHASE_ORDER', 'DELIVERY_NOTE', 'CREDIT_NOTE', 'CONTRACT', 'COA', 'SPEC_SHEET', 'CERTIFICATE'],
      skills: [
        { skill_type: 'document_classification', is_enabled: true, config: { model: 'multiclass-v3', threshold: 0.85 }, description: 'Document type classification for F&B supplier docs' },
      ],
      triggers: [
        { trigger_type: 'document_upload', conditions: [], is_enabled: true, description: 'Classify every uploaded document' },
      ],
      priority: 15,
    }),
    role: 'classifier',
    skills_summary: ['Multi-class document classification', 'CoA vs invoice distinction', 'Organic/halal/kosher cert detection', 'Multi-language layout analysis'],
    current_tasks: ['task-003'],
    completed_task_count: 1832,
    approval_mode: 'auto',
    reports_to: 'agent-007',
    direct_reports: [],
    workload_percent: 45,
    last_active_at: '2026-03-09T09:12:00Z',
    performance: { success_rate: 99.1, avg_completion_hours: 0.1, tasks_per_week: 305, error_count: 3, streak_days: 28 },
    email_events: [
      { id: 'ee-001', agent_id: 'agent-002', agent_name: 'DocClassifier', type: 'received', from: 'buchhaltung@rheinmuehle.de', subject: 'Invoice RM-2026-4521 for PO #FB-4500123456', document_count: 3, linked_issue_id: 'issue-003', timestamp: '2026-03-09T08:30:00Z' },
      { id: 'ee-002', agent_id: 'agent-002', agent_name: 'DocClassifier', type: 'classified', subject: 'Classified as Invoice (94% confidence)', linked_issue_id: 'issue-003', timestamp: '2026-03-09T08:31:00Z' },
      { id: 'ee-003', agent_id: 'agent-002', agent_name: 'DocClassifier', type: 'received', from: 'quality@cacaotropicale.ec', subject: 'CoA Batch #CT-88712 — Organic Cocoa Powder', document_count: 1, timestamp: '2026-03-09T07:15:00Z' },
      { id: 'ee-004', agent_id: 'agent-002', agent_name: 'DocClassifier', type: 'classified', subject: 'Classified as Certificate of Analysis (98% confidence)', timestamp: '2026-03-09T07:16:00Z' },
    ],
    email_stats: { received_today: 12, sent_today: 0, documents_created_today: 8 },
  },
  {
    agent: makeAgent({
      id: 'agent-003',
      name: 'ComplianceValidator',
      description: 'Validates supplier certificates (FSSC 22000, BRC, IFS), checks expiry dates, allergen declarations, and cross-references against regulatory requirements.',
      document_types: ['INVOICE', 'PURCHASE_ORDER', 'COA', 'CERTIFICATE'],
      skills: [
        { skill_type: 'field_validation', is_enabled: true, config: { strict_mode: true }, description: 'Food safety compliance validation' },
        { skill_type: 'master_data_lookup', is_enabled: true, config: { sources: ['infor_ln', 'vendor_db', 'fssc_registry'] }, description: 'Regulatory cross-reference' },
      ],
      triggers: [
        { trigger_type: 'document_status_change', conditions: [{ field: 'status', operator: 'eq', value: 'extracted' }], is_enabled: true, description: 'Validate after extraction completes' },
      ],
      priority: 8,
    }),
    role: 'validator',
    skills_summary: ['FSSC 22000 / BRC / IFS certificate validation', 'Allergen declaration cross-check', 'Expiry date monitoring', 'Lot traceability verification'],
    current_tasks: ['task-002', 'task-008', 'task-012'],
    completed_task_count: 589,
    approval_mode: 'hybrid',
    reports_to: 'agent-007',
    direct_reports: [],
    workload_percent: 88,
    last_active_at: '2026-03-09T09:05:00Z',
    performance: { success_rate: 91.5, avg_completion_hours: 1.2, tasks_per_week: 98, error_count: 51, streak_days: 3 },
  },
  {
    agent: makeAgent({
      id: 'agent-004',
      name: 'POMatchingAgent',
      description: '3-way match: ingredient/packaging PO -> goods receipt -> supplier invoice with lot/batch traceability and quantity tolerance checks.',
      document_types: ['INVOICE', 'PURCHASE_ORDER'],
      skills: [
        { skill_type: 'po_matching', is_enabled: true, config: { tolerance_percent: 2, match_strategy: '3-way' }, description: '3-way PO matching with lot traceability' },
      ],
      triggers: [
        { trigger_type: 'document_status_change', conditions: [{ field: 'status', operator: 'eq', value: 'validated' }], is_enabled: true, description: 'Match after validation passes' },
      ],
      priority: 7,
    }),
    role: 'specialist',
    skills_summary: ['3-way PO matching', 'Ingredient price variance detection', 'Batch/lot quantity reconciliation', 'GR/IR matching with Infor LN'],
    current_tasks: ['task-006', 'task-010'],
    completed_task_count: 412,
    approval_mode: 'manual',
    reports_to: 'agent-005',
    direct_reports: [],
    workload_percent: 63,
    last_active_at: '2026-03-09T08:30:00Z',
    performance: { success_rate: 94.8, avg_completion_hours: 2.4, tasks_per_week: 69, error_count: 22, streak_days: 7 },
  },
  {
    agent: makeAgent({
      id: 'agent-005',
      name: 'APReviewer',
      description: 'Reviews high-value ingredient purchases, validates supplier contracts, price agreements, and checks compliance before posting to Infor LN.',
      document_types: ['INVOICE', 'CREDIT_NOTE'],
      skills: [
        { skill_type: 'summarization', is_enabled: true, config: {}, description: 'Generate processing summary' },
        { skill_type: 'field_validation', is_enabled: true, config: { strict_mode: false }, description: 'Final review checks against supplier contracts' },
      ],
      triggers: [
        { trigger_type: 'workflow_step', conditions: [{ field: 'step', operator: 'eq', value: 'review' }], is_enabled: true, description: 'Triggered at review workflow step' },
      ],
      config: {
        max_retries: 3,
        timeout_seconds: 120,
        log_interactions: true,
        require_approval: true,
        model: 'gpt-4o',
        temperature: 0.1,
        max_tokens: 4096,
      },
      priority: 6,
    }),
    role: 'reviewer',
    skills_summary: ['Final invoice review', 'Supplier contract compliance', 'Audit trail generation', 'Infor LN posting readiness'],
    current_tasks: ['task-004', 'task-009'],
    completed_task_count: 318,
    approval_mode: 'manual',
    reports_to: 'agent-007',
    direct_reports: ['agent-004'],
    workload_percent: 55,
    last_active_at: '2026-03-09T07:50:00Z',
    performance: { success_rate: 98.7, avg_completion_hours: 3.1, tasks_per_week: 53, error_count: 4, streak_days: 21 },
  },
  {
    agent: makeAgent({
      id: 'agent-006',
      name: 'SupplierOnboarder',
      description: 'Onboards new ingredient and packaging suppliers, validates food safety certifications (FSSC 22000, BRC, IFS), and creates vendor master records in Infor LN.',
      document_types: ['CONTRACT', 'VENDOR_FORM', 'CERTIFICATE'],
      skills: [
        { skill_type: 'document_extraction', is_enabled: true, config: { fields: ['vendor_name', 'tax_id', 'bank_details', 'address', 'food_safety_cert', 'allergen_policy'] }, description: 'Supplier data extraction with F&B fields' },
        { skill_type: 'master_data_lookup', is_enabled: true, config: { sources: ['infor_ln'] }, description: 'Duplicate supplier check in Infor' },
      ],
      triggers: [
        { trigger_type: 'manual', conditions: [], is_enabled: true, description: 'Manually triggered for supplier onboarding' },
        { trigger_type: 'email_incoming', conditions: [{ field: 'subject', operator: 'contains', value: 'supplier registration' }], is_enabled: true, description: 'Auto-trigger from supplier registration emails' },
      ],
      priority: 5,
    }),
    role: 'specialist',
    skills_summary: ['Supplier data extraction', 'Food safety cert validation', 'GFSI certification check', 'Infor LN vendor master creation'],
    current_tasks: ['task-011'],
    completed_task_count: 96,
    approval_mode: 'manual',
    reports_to: 'agent-007',
    direct_reports: [],
    workload_percent: 22,
    last_active_at: '2026-03-08T16:20:00Z',
    performance: { success_rate: 100, avg_completion_hours: 6.5, tasks_per_week: 16, error_count: 0, streak_days: 96 },
    email_events: [
      { id: 'ee-005', agent_id: 'agent-006', agent_name: 'SupplierOnboarder', type: 'received', from: 'export@anatolianspice.com.tr', subject: 'Supplier Registration: Anatolian Spice Trading', document_count: 2, linked_issue_id: 'issue-011', timestamp: '2026-03-09T06:00:00Z' },
      { id: 'ee-006', agent_id: 'agent-006', agent_name: 'SupplierOnboarder', type: 'document_created', subject: 'Created vendor master record for Anatolian Spice Trading', linked_issue_id: 'issue-011', timestamp: '2026-03-09T06:15:00Z' },
    ],
    email_stats: { received_today: 3, sent_today: 0, documents_created_today: 2 },
  },
  {
    agent: makeAgent({
      id: 'agent-007',
      name: 'ProcessOrchestrator',
      description: 'Orchestrates end-to-end document flows across procurement, quality, and finance for F&B operations. Coordinates ingredient invoices, CoAs, and supplier certifications.',
      document_types: ['INVOICE', 'PURCHASE_ORDER', 'DELIVERY_NOTE', 'CREDIT_NOTE', 'CONTRACT', 'COA', 'CERTIFICATE'],
      skills: [
        { skill_type: 'custom_script', is_enabled: true, config: { script: 'workflow-routing-v2' }, description: 'Intelligent workflow routing for F&B document types' },
        { skill_type: 'summarization', is_enabled: true, config: {}, description: 'Pipeline status summaries' },
      ],
      triggers: [
        { trigger_type: 'document_upload', conditions: [], is_enabled: true, description: 'Orchestrate on every new document' },
        { trigger_type: 'scheduled', conditions: [], is_enabled: true, description: 'Hourly pipeline health check' },
      ],
      priority: 20,
    }),
    role: 'orchestrator',
    skills_summary: ['Workflow orchestration', 'Agent delegation', 'Pipeline monitoring', 'SLA tracking', 'Bottleneck detection'],
    current_tasks: ['task-007', 'task-013'],
    completed_task_count: 1104,
    approval_mode: 'auto',
    reports_to: undefined,
    direct_reports: ['agent-001', 'agent-002', 'agent-003', 'agent-005', 'agent-006', 'agent-008'],
    workload_percent: 38,
    last_active_at: '2026-03-09T09:15:00Z',
    performance: { success_rate: 99.8, avg_completion_hours: 0.3, tasks_per_week: 184, error_count: 2, streak_days: 45 },
    email_events: [
      { id: 'ee-007', agent_id: 'agent-007', agent_name: 'ProcessOrchestrator', type: 'sent', to: 'team@docbits.com', subject: 'Daily pipeline summary: 47 supplier docs processed', timestamp: '2026-03-09T09:00:00Z' },
      { id: 'ee-008', agent_id: 'agent-007', agent_name: 'ProcessOrchestrator', type: 'sent', to: 'alerts@docbits.com', subject: 'SLA warning: 3 ingredient invoices approaching deadline', timestamp: '2026-03-09T08:00:00Z' },
    ],
    email_stats: { received_today: 0, sent_today: 5, documents_created_today: 0 },
  },
  {
    agent: makeAgent({
      id: 'agent-008',
      name: 'TranslationAgent',
      description: 'Translates supplier documents (DE/FR/IT/ES) for multi-country F&B operations. Handles ingredient specs, supply contracts, and regulatory certificates.',
      document_types: ['INVOICE', 'PURCHASE_ORDER', 'CONTRACT', 'COA', 'CERTIFICATE'],
      skills: [
        { skill_type: 'translation', is_enabled: true, config: { supported_languages: ['en', 'de', 'fr', 'es', 'it', 'nl'] }, description: 'Multi-language translation for F&B documents' },
      ],
      triggers: [
        { trigger_type: 'document_status_change', conditions: [{ field: 'language', operator: 'neq', value: 'en' }], is_enabled: true, description: 'Translate non-English documents' },
      ],
      priority: 4,
    }),
    role: 'specialist',
    skills_summary: ['Multi-language translation', 'Ingredient terminology localization', 'Regulatory term standardization', 'Date/unit format normalization'],
    current_tasks: [],
    completed_task_count: 203,
    approval_mode: 'auto',
    reports_to: 'agent-007',
    direct_reports: [],
    workload_percent: 10,
    last_active_at: '2026-03-08T11:00:00Z',
    performance: { success_rate: 96.8, avg_completion_hours: 1.8, tasks_per_week: 34, error_count: 7, streak_days: 10 },
  },
];
